/**
 * lib/syncTruth.js — Founder "One Sync Truth" canonical facade.
 *
 * There are 2 independent offline-write engines in this app:
 *   1. offlineQueueV2.js  (v2, localStorage `fop_offline_queue_v2`) —
 *      markups / punch / objects / pins / photos / inspections / workflows
 *   2. syncQueue.js       (v3, IndexedDB `fop_sync_queue_v1`)   — utility
 *      intelligence + photo timeline ops
 *
 * FP-034 (2026-09-09 audit remediation): a THIRD engine, offlineQueue.js
 * (v1, IndexedDB `fop_offline`), used to exist for pins only. It had
 * none of v2's identity-scoping, durable-persistence-honesty,
 * concurrent-race-safety, dead-letter-visibility or server-side
 * idempotency protections — a live, untested path left behind a
 * general "offline fixed" claim. Pins were migrated onto v2's
 * `pin.create` op (see SheetViewer.jsx) and v1 was retired entirely
 * rather than left as an unused duplicate that could silently
 * resurface. Do not re-introduce it.
 *
 * Every UI surface that shows sync/pending/offline state, or drains the
 * queues, MUST go through this module. Do not re-implement combination
 * logic in a component — that is exactly how the SyncBadge / viewer
 * SyncPill divergence happened (one had auto-drain-on-reconnect, the
 * other didn't; a queued pin could silently sit unsynced forever).
 *
 * Also owns:
 *   - "actually synced" vs "merely reconnected" distinction, so callers
 *     can fire a success toast ONLY after a real drain completed with
 *     items that were genuinely pending — never just because
 *     `navigator.onLine` flipped true.
 *   - Stuck-pending detection: if the oldest queued item has been
 *     pending longer than STUCK_THRESHOLD_MS while the browser reports
 *     itself online, report lightweight METADATA (engine, age, attempt
 *     count — never payload content) to the backend so ForgedOps ops
 *     can see field data at risk without inspecting customer content.
 */
import {
  onChange as onSyncChange,
  snapshot as syncSnapshot,
  drainNow as syncDrain,
  listAll as listSyncQueueAll,
} from "./syncQueue";
import {
  queueState as queueStateV2,
  flush as flushV2,
  listQueue as listQueueV2,
} from "./offlineQueueV2";
import { api } from "./api";

const STUCK_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes online + still pending = stuck
const STUCK_REPORT_INTERVAL_MS = 2 * 60 * 1000; // don't beacon more than every 2 min

export async function getCombinedSnapshot() {
  const [syncQ, v2] = await Promise.all([
    syncSnapshot(),
    Promise.resolve(queueStateV2()),
  ]);
  const pending = (syncQ.pending || 0) + (v2.pending || 0);
  // FP-035 (2026-09-09 audit remediation) — v2.pending EXCLUDES
  // dead-lettered (permanently rejected) entries by definition
  // (queueStateV2 filters `!e.permanent`, GH-07 2026-10-01). Without
  // this, a tenant with 3 dead-lettered markups and 0 retriable pending
  // work would compute `pending === 0` and report `state: "synced"` — the UI would show
  // a calm "Synced ✓" while real field work sits permanently stuck,
  // never retried, never surfaced. Dead letters must always be
  // visible and must always prevent a false "synced" state.
  const dead = v2.dead || 0;
  let state = syncQ.state;
  if (pending === 0 && dead === 0 && state === "synced") return { state: "synced", pending: 0, dead: 0 };
  if (dead > 0 && pending === 0) {
    // Nothing left to retry automatically, but real unsynced work
    // exists and needs a human decision (retry or discard) — never
    // "synced".
    return { state: "attention_needed", pending: 0, dead };
  }
  if (state === "synced" && (pending > 0 || dead > 0)) {
    state = typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "pending";
  }
  return { state, pending, dead };
}

let _draining = false;
export async function drainAll() {
  if (_draining) return { succeeded: 0, failed: 0 };
  _draining = true;
  try {
    const [a, b] = await Promise.all([
      syncDrain(),
      (async () => { try { return await flushV2(); } catch { return { succeeded: 0, failed: 0 }; } })(),
    ]);
    return {
      succeeded: (a?.succeeded || 0) + (b?.succeeded || 0),
      failed: (a?.failed || 0) + (b?.failed || 0),
    };
  } finally {
    _draining = false;
  }
}

export function onAnyChange(fn) {
  const off1 = onSyncChange(fn);
  const onV2 = () => fn();
  window.addEventListener("forgedops:offline-queue", onV2);
  return () => {
    off1();
    window.removeEventListener("forgedops:offline-queue", onV2);
  };
}

// ---------------------------------------------------------------------
// "Actually synced" detection — distinguishes CONNECTED from SYNCED.
// A caller marks that it has observed real pending work (offline or
// online-but-queued), then calls noteSnapshot() on every refresh; the
// module fires `forgedops:sync-success` exactly once, only when pending
// work that was genuinely outstanding has fully drained.
// ---------------------------------------------------------------------
let _hadRealPendingWork = false;

export function noteSnapshot(snap) {
  if ((snap.pending > 0 || snap.dead > 0) && (snap.state === "offline" || snap.state === "pending" || snap.state === "syncing" || snap.state === "error" || snap.state === "attention_needed")) {
    _hadRealPendingWork = true;
  }
  if (snap.state === "synced" && _hadRealPendingWork) {
    _hadRealPendingWork = false;
    try {
      window.dispatchEvent(new CustomEvent("forgedops:sync-success"));
    } catch { /* non-essential */ }
  }
}

// ---------------------------------------------------------------------
// Stuck-pending health beacon — metadata only, never payload content.
// ---------------------------------------------------------------------
let _lastStuckReportAt = 0;

async function _oldestPendingAgeMs() {
  const [syncEntries, v2Entries] = await Promise.all([
    listSyncQueueAll(),
    Promise.resolve(listQueueV2()),
  ]);
  const timestamps = [
    ...syncEntries.map((e) => e.created_at),
    ...v2Entries.map((e) => e.queued_at),
  ].filter(Boolean);
  const maxAttempts = Math.max(
    0,
    ...syncEntries.map((e) => e.attempts || 0),
    ...v2Entries.map((e) => e.attempts || 0)
  );
  if (timestamps.length === 0) return { ageMs: 0, maxAttempts: 0, engine: null };
  const oldest = timestamps.reduce((min, ts) => {
    const t = new Date(ts).getTime();
    return isNaN(t) ? min : Math.min(min, t);
  }, Infinity);
  if (!isFinite(oldest)) return { ageMs: 0, maxAttempts, engine: null };
  // Identify which engine owns the oldest item (best-effort, for the beacon label).
  let engine = "sync_queue_v3";
  if (v2Entries.some((e) => e.queued_at && new Date(e.queued_at).getTime() === oldest)) engine = "field_queue_v2";
  return { ageMs: Date.now() - oldest, maxAttempts, engine };
}

/**
 * Called periodically (e.g. from a single always-mounted listener). If
 * there's pending work that has aged past the stuck threshold while the
 * browser reports itself online, report metadata to the backend so ops
 * can see it. Also actively clears the alert once pending hits 0.
 */
export async function checkStuckPending(pendingCount) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const now = Date.now();
  if (pendingCount === 0) {
    // Clear any previously-reported alert for this user (cheap, best-effort).
    if (_lastStuckReportAt !== 0) {
      _lastStuckReportAt = 0;
      try {
        await api.post("/sync-health/report", {
          engine: "field_queue_v2", pending: 0, oldest_age_seconds: 0,
        });
      } catch { /* best-effort */ }
    }
    return;
  }
  const { ageMs, maxAttempts, engine } = await _oldestPendingAgeMs();
  if (ageMs < STUCK_THRESHOLD_MS) return;
  if (now - _lastStuckReportAt < STUCK_REPORT_INTERVAL_MS) return;
  _lastStuckReportAt = now;
  try {
    await api.post("/sync-health/report", {
      engine: engine || "sync_queue_v3",
      pending: pendingCount,
      oldest_age_seconds: Math.round(ageMs / 1000),
      max_attempts: maxAttempts,
    });
  } catch { /* telemetry must never break the UI */ }
}

